import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import { useChatContext } from '../../../contexts/useChatContext';
import { ChatConversation } from './useChat';
import { notify } from '../../../utils/toast';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useAssignment } from '../../../contexts/assignment/AssignmentContext';
import { useChatFavorites } from './useChatFavorites';
import { emitConversationMetaChanged } from '../utils/partykitSocket';
import { generateJidVariants } from '../../../utils/phoneUtils';
import {
    resolveConversationConnectionScope,
    scopeConversationQuery,
} from '../lib/conversationScope';

export function useContactPanelConversation(conversation: ChatConversation | undefined, connectionId?: string) {
    const { updateConversation } = useChatContext();
    const { currentOrganization } = useOrganization();
    const { assignUser } = useAssignment();
    const { checkAndAutoUnfavorite } = useChatFavorites();

    // Estados para informações da conversa
    const [conversationResponsible, setConversationResponsible] = useState<string | null>(null);
    const [conversationTags, setConversationTags] = useState<string[]>([]);

    // Sincroniza estados com a conversa ativa
    useEffect(() => {
        if (conversation) {
            setConversationResponsible(prev => prev !== conversation.assignedTo ? (conversation.assignedTo || null) : prev);
            setConversationTags(prev => {
                const newTags = conversation.tags || [];
                // Simple array comparison
                if (prev.length !== newTags.length || !prev.every((t, i) => t === newTags[i])) {
                    return newTags;
                }
                return prev;
            });
        }
    }, [conversation]);

    // Busca ID do banco (variantes de JID — evita atualizar a linha errada)
    const getConversationDbId = useCallback(async (jid: string): Promise<string | null> => {
        const scopeConnectionId = resolveConversationConnectionScope(jid, {
            conversationConnectionId: conversation?.connectionId,
            selectedConnectionId: connectionId,
        });
        let query = supabase
            .from('chat_conversations')
            .select('id')
            .in('remote_jid', generateJidVariants(jid))
            .order('last_message_time', { ascending: false })
            .limit(1);
        query = scopeConversationQuery(query, scopeConnectionId, currentOrganization?.id);

        const { data, error } = await query.maybeSingle();
        if (error) {
            console.error('[ContactPanel] Erro ao buscar DB ID da conversa:', error);
            return null;
        }
        return data?.id || null;
    }, [conversation?.connectionId, connectionId, currentOrganization?.id]);

    // Handler para mudança de responsável
    const handleConversationResponsibleChange = useCallback(async (userId: string | null) => {
        if (!conversation?.id) return;

        // Toast de feedback ao usuário
        notify.info('🔄 Transferindo conversa...');

        const dbId = await getConversationDbId(conversation.id);

        if (!dbId) {
            notify.warning('Conversa não encontrada');
            return;
        }

        // IG não tem telefone WA — syncByPhone só para WhatsApp (mesmo contrato das actions).
        const phone = conversation.id.startsWith('ig:')
            ? undefined
            : conversation.id.replace('@s.whatsapp.net', '').replace(/\D/g, '');

        // Usa useAssignment para sincronizar tudo
        const success = await assignUser({
            entityType: 'conversation',
            entityId: dbId,
            userId: userId,
            phone,
            silent: false // Mostra toast
        });

        if (success) {
            // Update UI ONLY if database update was completely successful
            setConversationResponsible(userId);
            updateConversation(conversation.id, { assignedTo: userId || undefined });

            // Auto-desfavoritar usando regra centralizada
            await checkAndAutoUnfavorite(conversation.id);

            if (currentOrganization?.id) {
                window.dispatchEvent(new CustomEvent('assignment:changed', {
                    detail: {
                        remoteJid: conversation.id,
                        userId,
                        organizationId: currentOrganization.id,
                    },
                }));
            }
        } else {
            notify.error('Erro ao transferir a conversa. Tente novamente.');
        }
    }, [conversation, updateConversation, getConversationDbId, assignUser, checkAndAutoUnfavorite, currentOrganization?.id]);

    const handleConversationTagsChange = async (tagIds: string[]) => {
        setConversationTags(tagIds);

        if (conversation?.id) {
            updateConversation(conversation.id, { tags: tagIds });

            try {
                // SEGURANÇA: Filtra por organization_id para evitar cross-org leak
                const dbId = await getConversationDbId(conversation.id);
                if (!dbId) {
                    notify.warning('Conversa não encontrada');
                    return;
                }

                const { error } = await supabase
                    .from('chat_conversations')
                    .update({ tags: tagIds })
                    .eq('id', dbId);

                if (error) {
                    if (error.code === '42703') {
                        notify.warning('Recurso de tags ainda não disponível no banco de dados');
                    } else {
                        notify.error('Erro ao atualizar tags');
                    }
                } else {
                    notify.success('Tags atualizadas');
                    if (currentOrganization?.id) {
                        const phone = conversation.id.startsWith('ig:')
                            ? undefined
                            : conversation.id.replace('@s.whatsapp.net', '').replace(/\D/g, '');
                        emitConversationMetaChanged({
                            organizationId: currentOrganization.id,
                            phone: phone || undefined,
                            remoteJid: conversation.id,
                        });
                    }
                }
            } catch (_err) {
                notify.error('Erro ao salvar tags');
            }
        }
    };

    return {
        conversationResponsible,
        conversationTags,
        handleConversationResponsibleChange,
        handleConversationTagsChange
    };
}
